import React, { useState } from "react";
import { API_URLS } from "../../config/apiUrls";
import {
  Card,
  CardContent,
  Typography,
  Divider,
  Box,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  //  Grid, // REMOVIDO: Eliminando o componente Grid
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

const toDateTimeLocal = (value: string | null): string => {
  if (!value) return "";
  const date = new Date(value);
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 16);
};

const formatPeriod = (startsAt: string | null, endsAt: string | null) => {
  const format = (value: string) => new Date(value).toLocaleString("pt-BR");
  if (startsAt && endsAt) return `${format(startsAt)} até ${format(endsAt)}`;
  if (startsAt) return `A partir de ${format(startsAt)}`;
  if (endsAt) return `Até ${format(endsAt)}`;
  return "Sem agendamento";
};

const getPlanStatus = (
  isActive: boolean,
  startsAt: string | null,
  endsAt: string | null
) => {
  const now = Date.now();
  if (!isActive) return { label: "Inativo", color: "default" as const };
  if (startsAt && new Date(startsAt).getTime() > now) {
    return { label: "Agendado", color: "info" as const };
  }
  if (endsAt && new Date(endsAt).getTime() < now) {
    return { label: "Encerrado", color: "warning" as const };
  }
  return { label: "Ativo", color: "success" as const };
};

interface PlanoProps {
  id: string;
  name: string;
  description: string;
  price: string;
  duration: number;
  isActive: boolean;
  startsAt: string | null;
  endsAt: string | null;
  limitPerUser: number | null;
  token: string;
  onPlanUpdate: () => void; // Função para atualizar a lista de planos
}

const PlanosCard: React.FC<PlanoProps> = ({
  id,
  name,
  description,
  price,
  duration,
  isActive,
  startsAt,
  endsAt,
  limitPerUser,
  token,
  onPlanUpdate,
}) => {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedName, setEditedName] = useState<string>(name);
  const [editedDescription, setEditedDescription] =
    useState<string>(description);
  const [editedPrice, setEditedPrice] = useState<string>(price);
  const [editedDuration, setEditedDuration] = useState<number>(duration);
  const [editedIsActive, setEditedIsActive] = useState<boolean>(isActive);
  const [editedStartsAt, setEditedStartsAt] = useState<string>(
    toDateTimeLocal(startsAt)
  );
  const [editedEndsAt, setEditedEndsAt] = useState<string>(
    toDateTimeLocal(endsAt)
  );
  const [editedLimitPerUser, setEditedLimitPerUser] = useState<number>(
    limitPerUser ?? 2
  );

  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);

  // Estado para controlar o Snackbar
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  // Função para mostrar o Snackbar
  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setOpenSnackbar(true);
  };

  const handleEditClick = () => {
    setEditedName(name);
    setEditedDescription(description);
    setEditedPrice(price);
    setEditedDuration(duration);
    setEditedIsActive(isActive);
    setEditedStartsAt(toDateTimeLocal(startsAt));
    setEditedEndsAt(toDateTimeLocal(endsAt));
    setEditedLimitPerUser(limitPerUser ?? 2);
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (Number(editedPrice) === 0 && editedLimitPerUser < 1) {
      showSnackbar("O limite por usuário deve ser maior que zero.", "error");
      return;
    }

    const updatedPlan = {
      name: editedName,
      description: editedDescription,
      // Garante que o preço e a duração são enviados como números
      price: parseFloat(editedPrice),
      duration: editedDuration,
      isActive: editedIsActive,
      startsAt: editedStartsAt
        ? new Date(editedStartsAt).toISOString()
        : null,
      endsAt: editedEndsAt ? new Date(editedEndsAt).toISOString() : null,
      limitPerUser:
        Number(editedPrice) === 0 ? editedLimitPerUser : null,
    };

    if (
      editedStartsAt &&
      editedEndsAt &&
      new Date(editedEndsAt) <= new Date(editedStartsAt)
    ) {
      showSnackbar("A data final deve ser posterior à data inicial.", "error");
      return;
    }

    try {
      const response = await fetch(`${API_URLS.planos}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedPlan),
      });

      if (!response.ok) {
        throw new Error("Erro ao atualizar o plano.");
      }

      showSnackbar("Plano atualizado com sucesso!", "success");
      setIsEditing(false);
      onPlanUpdate(); // Chama a função para atualizar a lista de planos
    } catch (err) {
      showSnackbar("Erro ao atualizar o plano. Tente novamente.", "error");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedName(name);
    setEditedDescription(description);
    setEditedPrice(price);
    setEditedDuration(duration);
    setEditedIsActive(isActive);
    setEditedStartsAt(toDateTimeLocal(startsAt));
    setEditedEndsAt(toDateTimeLocal(endsAt));
    setEditedLimitPerUser(limitPerUser ?? 2);
  };

  const handleToggleActive = async () => {
    try {
      const response = await fetch(`${API_URLS.planos}/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isActive: !isActive }),
      });
      if (!response.ok) throw new Error();
      showSnackbar(
        isActive ? "Plano desativado." : "Plano ativado com novo limite.",
        "success"
      );
      onPlanUpdate();
    } catch {
      showSnackbar("Erro ao alterar a ativação do plano.", "error");
    }
  };

  const status = getPlanStatus(isActive, startsAt, endsAt);

  const handleDeleteClick = () => {
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await fetch(`${API_URLS.planos}/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Erro ao excluir o plano.");
      }

      showSnackbar("Plano excluído com sucesso!", "success");
      onPlanUpdate(); // Atualiza a lista de planos após a exclusão
    } catch (err) {
      showSnackbar("Erro ao excluir o plano. Tente novamente.", "error");
    } finally {
      setOpenDeleteDialog(false);
    }
  };

  const handleDeleteCancel = () => {
    setOpenDeleteDialog(false);
  };

  return (
    // Substitui <Grid item ...> por <Box> com estilos de largura e margem
    <Box
      sx={{
        marginBottom: "20px",
        padding: 2, // Simula margem externa se este Box estiver dentro de um flex/grid container maior
        // Larguras responsivas replicando md=8 (2/3) e lg=6 (1/2) do grid de 12
        width: {
          xs: "100%",
          md: "40%",
          lg: "30%",
        },
      }}
    >
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 2,
          // Remove a margin 2 aqui, pois o padding do Box pai já define o espaçamento.
        }}
      >
        <CardContent>
          {/* Nome */}
          {isEditing ? (
            <TextField
              label="Nome do Plano"
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              fullWidth
              margin="normal"
            />
          ) : (
            <Box display="flex" justifyContent="space-between" gap={1}>
              <Typography variant="h6" component="div" gutterBottom>
                {name}
              </Typography>
              <Chip label={status.label} color={status.color} size="small" />
            </Box>
          )}

          {/* Descrição */}
          {isEditing ? (
            <TextField
              label="Descrição do Plano"
              value={editedDescription}
              onChange={(e) => setEditedDescription(e.target.value)}
              fullWidth
              margin="normal"
              multiline
              rows={3}
            />
          ) : (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          )}

          <Divider sx={{ marginY: 2 }} />

          {/* Preço */}
          {isEditing ? (
            <TextField
              label="Preço"
              type="number"
              value={editedPrice}
              onChange={(e) => setEditedPrice(e.target.value)}
              fullWidth
              margin="normal"
              inputProps={{ min: 0, step: "0.01" }}
            />
          ) : (
            <Typography variant="h5" color="primary">
              {Number(price) === 0
                ? "Grátis"
                : `R$ ${parseFloat(price).toFixed(2)}`}
            </Typography>
          )}

          {isEditing && Number(editedPrice) === 0 && (
            <TextField
              label="Limite por usuário"
              type="number"
              value={editedLimitPerUser}
              onChange={(e) => setEditedLimitPerUser(Number(e.target.value))}
              fullWidth
              margin="normal"
              inputProps={{ min: 1, step: 1 }}
            />
          )}

          {/* Duração */}
          {isEditing ? (
            <TextField
              label="Duração (dias)"
              type="number"
              value={editedDuration === 0 ? "" : editedDuration}
              onChange={(e) => setEditedDuration(Number(e.target.value))}
              fullWidth
              margin="normal"
            />
          ) : (
            <>
              <Typography variant="body2" color="text.secondary">
                Duração: {duration} dias
              </Typography>
              {Number(price) === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Limite: {limitPerUser ?? 2} anúncios por usuário
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Período: {formatPeriod(startsAt, endsAt)}
              </Typography>
            </>
          )}

          {isEditing && (
            <>
              <TextField
                label="Início da disponibilidade"
                type="datetime-local"
                value={editedStartsAt}
                onChange={(e) => setEditedStartsAt(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Fim da disponibilidade"
                type="datetime-local"
                value={editedEndsAt}
                onChange={(e) => setEditedEndsAt(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editedIsActive}
                    onChange={(e) => setEditedIsActive(e.target.checked)}
                  />
                }
                label="Plano habilitado"
              />
            </>
          )}

          {/* Editar e Deletar Buttons */}
          {!isEditing && (
            <>
              <IconButton onClick={handleEditClick} color="primary">
                <EditIcon />
              </IconButton>
              <IconButton onClick={handleDeleteClick} color="error">
                <DeleteIcon />
              </IconButton>
              <Button size="small" onClick={handleToggleActive}>
                {isActive ? "Desativar" : "Ativar"}
              </Button>
            </>
          )}

          {/* Save/Cancel buttons */}
          {isEditing && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 2,
              }}
            >
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCancel}
              >
                Cancelar
              </Button>
              <Button variant="contained" color="primary" onClick={handleSave}>
                Salvar
              </Button>
            </Box>
          )}
        </CardContent>

        {/* Dialog para confirmação de exclusão */}
        <Dialog open={openDeleteDialog} onClose={handleDeleteCancel}>
          <DialogTitle>Excluir Plano</DialogTitle>
          <DialogContent>
            <Typography>Tem certeza que deseja excluir este plano?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteCancel} color="primary">
              Cancelar
            </Button>
            <Button onClick={handleDeleteConfirm} color="error">
              Excluir
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para mensagens de sucesso ou erro */}
        <Snackbar open={openSnackbar} onClose={() => setOpenSnackbar(false)}>
          <Alert
            onClose={() => setOpenSnackbar(false)}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Card>
    </Box>
  );
};

export default PlanosCard;
